import type { Risk } from '../../types/api';
import { extractRiskCityName } from '../../functions/geospatial';
import '../../css/monthRangeSlider.css';
import {
  publicationBoundsFromRisks,
  publicationDayMs,
  formatPublicationDayLabel,
  dayIndexToMs,
  type PublicationBounds,
} from './geospatialPublicationRange';

export interface GeospatialFilterOptions {
  countries: string[];
  cities: string[];
  sourceTypes: string[];
  originators: string[];
  operatingModes: string[];
  riskCategories: string[];
  impactTargets: string[];
  publicationBounds: PublicationBounds;
}

export interface GeospatialSidebarFiltersProps {
  showAllRisks: boolean;
  onShowAllRisksChange: (showAll: boolean) => void;
  toggleDisabled: boolean;
  toggleHint?: string;
  country: string;
  onCountryChange: (country: string) => void;
  city: string;
  onCityChange: (city: string) => void;
  sourceType: string;
  onSourceTypeChange: (v: string) => void;
  originator: string;
  onOriginatorChange: (v: string) => void;
  operatingMode: string;
  onOperatingModeChange: (mode: string) => void;
  riskCategory: string;
  onRiskCategoryChange: (category: string) => void;
  impactTarget: string;
  onImpactTargetChange: (v: string) => void;
  /** Inclusive day indices into options.publicationBounds (0 .. dayCount-1) */
  publicationFromIndex: number;
  publicationToIndex: number;
  onPublicationFromIndexChange: (index: number) => void;
  onPublicationToIndexChange: (index: number) => void;
  options: GeospatialFilterOptions;
}

export function GeospatialSidebarFilters({
  showAllRisks,
  onShowAllRisksChange,
  toggleDisabled,
  toggleHint,
  country,
  onCountryChange,
  city,
  onCityChange,
  sourceType,
  onSourceTypeChange,
  originator,
  onOriginatorChange,
  operatingMode,
  onOperatingModeChange,
  riskCategory,
  onRiskCategoryChange,
  impactTarget,
  onImpactTargetChange,
  publicationFromIndex,
  publicationToIndex,
  onPublicationFromIndexChange,
  onPublicationToIndexChange,
  options,
}: GeospatialSidebarFiltersProps) {
  const bounds = options.publicationBounds;
  const maxIdx = Math.max(0, bounds.dayCount - 1);
  const fromIdx = Math.min(publicationFromIndex, maxIdx);
  const toIdx = Math.min(Math.max(publicationToIndex, fromIdx), maxIdx);
  const fromMs = dayIndexToMs(bounds, fromIdx);
  const toMs = dayIndexToMs(bounds, toIdx);
  const singleDay = bounds.dayCount <= 1;
  const fromPct = maxIdx > 0 ? (fromIdx / maxIdx) * 100 : 0;
  const toPct = maxIdx > 0 ? (toIdx / maxIdx) * 100 : 100;

  const onFromInput = (v: number) => {
    onPublicationFromIndexChange(v);
    if (v > publicationToIndex) onPublicationToIndexChange(v);
  };
  const onToInput = (v: number) => {
    onPublicationToIndexChange(v);
    if (v < publicationFromIndex) onPublicationFromIndexChange(v);
  };

  return (
    <aside className="geospatial-sidebar" aria-label="Map filters">
      <h2 className="geospatial-sidebar__title">Filters</h2>

      <div className="geospatial-sidebar__field">
        <span className="geospatial-sidebar__label" id="geo-scope-label">
          Scope
        </span>
        <div
          className="geospatial-sidebar__toggle"
          role="group"
          aria-labelledby="geo-scope-label"
        >
          <button
            type="button"
            className={`geospatial-sidebar__toggle-btn ${showAllRisks ? 'is-active' : ''}`}
            onClick={() => onShowAllRisksChange(true)}
            aria-pressed={showAllRisks}
            title="All risks and opportunities"
          >
            All
          </button>
          <button
            type="button"
            className={`geospatial-sidebar__toggle-btn ${!showAllRisks ? 'is-active' : ''}`}
            onClick={() => !toggleDisabled && onShowAllRisksChange(false)}
            disabled={toggleDisabled}
            aria-pressed={!showAllRisks}
            title={toggleHint || 'Risks in your units’ countries only'}
          >
            My units
          </button>
        </div>
        {toggleDisabled && (
          <p className="geospatial-sidebar__micro">Sign in with a linked account to filter by your units.</p>
        )}
      </div>

      <div className="geospatial-sidebar__field">
        <span className="geospatial-sidebar__label" id="geo-pub-heading">
          Publication date
        </span>
        <div
          className="geospatial-sidebar__date-range"
          role="group"
          aria-labelledby="geo-pub-heading"
        >
          {!singleDay ? (
            <div
              className="month-range-slider month-range-slider--pub compact"
              style={
                {
                  '--pub-from-pct': `${fromPct}%`,
                  '--pub-to-pct': `${toPct}%`,
                } as Record<string, string>
              }
            >
              <div
                className="month-range-slider__label"
                style={{ color: 'var(--geo-label-color, #334155)' }}
              >
                {formatPublicationDayLabel(fromMs)} → {formatPublicationDayLabel(toMs)}
              </div>
              <div className="month-range-slider__track-wrap">
                <div className="month-range-slider__track" />
                <div className="month-range-slider__fill month-range-slider__fill--pub" aria-hidden />
                <input
                  id="geo-pub-from"
                  type="range"
                  min={0}
                  max={maxIdx}
                  step={1}
                  value={fromIdx}
                  onInput={(e) => onFromInput(Number((e.target as HTMLInputElement).value))}
                  className="month-range-slider__input month-range-slider__input--start"
                  aria-label="Publication range start"
                  aria-valuetext={formatPublicationDayLabel(fromMs)}
                />
                <input
                  id="geo-pub-to"
                  type="range"
                  min={0}
                  max={maxIdx}
                  step={1}
                  value={toIdx}
                  onInput={(e) => onToInput(Number((e.target as HTMLInputElement).value))}
                  className="month-range-slider__input month-range-slider__input--end"
                  aria-label="Publication range end"
                  aria-valuetext={formatPublicationDayLabel(toMs)}
                />
              </div>
            </div>
          ) : (
            <p className="geospatial-sidebar__micro">Single day in current dataset.</p>
          )}
        </div>
      </div>

      <div className="geospatial-sidebar__field">
        <div className="geospatial-sidebar__inline-titled-row" role="group" aria-label="Source filters">
          <div className="geospatial-sidebar__inline-titled-cell">
            <label className="geospatial-sidebar__label" htmlFor="geo-source-type">
              Source type
            </label>
            <select
              id="geo-source-type"
              className="geospatial-sidebar__select"
              value={sourceType}
              onChange={(e) => onSourceTypeChange(e.currentTarget.value)}
            >
              <option value="">All source types</option>
              {options.sourceTypes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="geospatial-sidebar__inline-titled-cell">
            <label className="geospatial-sidebar__label" htmlFor="geo-originator">
              Originator
            </label>
            <select
              id="geo-originator"
              className="geospatial-sidebar__select"
              value={originator}
              onChange={(e) => onOriginatorChange(e.currentTarget.value)}
            >
              <option value="">All originators</option>
              {options.originators.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="geospatial-sidebar__field">
        <span className="geospatial-sidebar__label" id="geo-coverage-heading">
          Coverage area
        </span>
        <div
          className="geospatial-sidebar__coverage-inline"
          role="group"
          aria-labelledby="geo-coverage-heading"
        >
          <select
            id="geo-country"
            className="geospatial-sidebar__select geospatial-sidebar__select--inline"
            value={country}
            onChange={(e) => onCountryChange(e.currentTarget.value)}
            aria-label="Country"
          >
            <option value="">All countries</option>
            {options.countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            id="geo-city"
            className="geospatial-sidebar__select geospatial-sidebar__select--inline"
            value={city}
            onChange={(e) => onCityChange(e.currentTarget.value)}
            aria-label="City"
          >
            <option value="">All cities</option>
            {options.cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="geospatial-sidebar__field">
        <div className="geospatial-sidebar__inline-titled-row" role="group" aria-label="Model and category filters">
          <div className="geospatial-sidebar__inline-titled-cell">
            <label className="geospatial-sidebar__label" htmlFor="geo-operating">
              Operating model
            </label>
            <select
              id="geo-operating"
              className="geospatial-sidebar__select"
              value={operatingMode}
              onChange={(e) => onOperatingModeChange(e.currentTarget.value)}
            >
              <option value="">All models</option>
              {options.operatingModes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="geospatial-sidebar__inline-titled-cell">
            <label className="geospatial-sidebar__label" htmlFor="geo-category">
              Risk category
            </label>
            <select
              id="geo-category"
              className="geospatial-sidebar__select"
              value={riskCategory}
              onChange={(e) => onRiskCategoryChange(e.currentTarget.value)}
            >
              <option value="">All categories</option>
              {options.riskCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="geospatial-sidebar__field">
        <label className="geospatial-sidebar__label" htmlFor="geo-impact">
          Impact target
        </label>
        <select
          id="geo-impact"
          className="geospatial-sidebar__select"
          value={impactTarget}
          onChange={(e) => onImpactTargetChange(e.currentTarget.value)}
        >
          <option value="">All impact targets</option>
          {options.impactTargets.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
    </aside>
  );
}

export function buildCityOptionsFromRisks(risks: Risk[], countryFilter: string): string[] {
  const citySet = new Set<string>();
  const countryTrim = (countryFilter || '').trim();
  for (const r of risks) {
    if (countryTrim && (r.country || '').trim() !== countryTrim) continue;
    const ct = extractRiskCityName(r.location);
    if (ct) citySet.add(ct);
  }
  return [...citySet].sort((a, b) => a.localeCompare(b));
}

export function buildFilterOptionsFromRisks(
  risks: Risk[],
  countryFilterForCities: string = ''
): GeospatialFilterOptions {
  const countrySet = new Set<string>();
  const sourceTypeSet = new Set<string>();
  const originatorSet = new Set<string>();
  const modeSet = new Set<string>();
  const catSet = new Set<string>();
  const impactSet = new Set<string>();
  for (const r of risks) {
    const c = (r.country || '').trim();
    if (c) countrySet.add(c);
    const st = (r.source_type || '').trim();
    if (st) sourceTypeSet.add(st);
    const org = (r.originator || '').trim();
    if (org) originatorSet.add(org);
    const mode = (r.operating_mode || '').trim();
    if (mode) modeSet.add(mode);
    const cat = (r.risk_category || '').trim();
    if (cat) catSet.add(cat);
    const im = (r.impact_target || '').trim();
    if (im) impactSet.add(im);
  }
  return {
    countries: [...countrySet].sort((a, b) => a.localeCompare(b)),
    cities: buildCityOptionsFromRisks(risks, countryFilterForCities),
    sourceTypes: [...sourceTypeSet].sort((a, b) => a.localeCompare(b)),
    originators: [...originatorSet].sort((a, b) => a.localeCompare(b)),
    operatingModes: [...modeSet].sort((a, b) => a.localeCompare(b)),
    riskCategories: [...catSet].sort((a, b) => a.localeCompare(b)),
    impactTargets: [...impactSet].sort((a, b) => a.localeCompare(b)),
    publicationBounds: publicationBoundsFromRisks(risks),
  };
}

export function applyGeospatialFilters(
  risks: Risk[],
  country: string,
  city: string,
  sourceType: string,
  originator: string,
  operatingMode: string,
  riskCategory: string,
  impactTarget: string,
  publicationFromDayMs: number,
  publicationToDayMs: number
): Risk[] {
  return risks.filter((r) => {
    if (country && (r.country || '').trim() !== country) return false;
    if (city && extractRiskCityName(r.location) !== city) return false;
    if (sourceType && (r.source_type || '').trim() !== sourceType) return false;
    if (originator && (r.originator || '').trim() !== originator) return false;
    if (operatingMode && (r.operating_mode || '').trim() !== operatingMode) return false;
    if (riskCategory && (r.risk_category || '').trim() !== riskCategory) return false;
    if (impactTarget && (r.impact_target || '').trim() !== impactTarget) return false;
    const day = publicationDayMs(r.publication_date);
    if (day != null && (day < publicationFromDayMs || day > publicationToDayMs)) return false;
    return true;
  });
}
